import { httpClient } from "@/shared/services/http";

import { IUser } from "@/modules/client/clients.interface";
import { makeReadableName } from "@/shared/utils/common.utils";

/**
 * @description
 * * ADMIN - can invite candidates, change settings, see all reports.
 * * RECRUITER - can invite candidates and view reports on this assessment.
 * * INVITER - can only view assessment and invite candidates.
 * * HIRING_MANAGER - only has view permissions on this assessment - can see all tabs, report but can't edit anything and can't invite users.
 * */
export enum AssessmentRoleEnum {
  ADMIN = "admin",
  RECRUITER = "recruiter",
  INVITER = "inviter",
  HIRING_MANAGER = "hiring_manager",
}

export const AssessmentRolesDescription = {
  [AssessmentRoleEnum.RECRUITER]: "can both invite candidates and view reports on this assessment.",
  [AssessmentRoleEnum.INVITER]: "can only invite candidates to this assessment.",
  [AssessmentRoleEnum.HIRING_MANAGER]: "can only view reports on this assessment.",
};

// Exclude ADMIN role as it's only set automatically by the API
export const assessmentRoleDefinitions = Object.values(AssessmentRoleEnum)
  .filter((role) => role !== AssessmentRoleEnum.ADMIN)
  .map((role) => ({
    name: makeReadableName(role),
    description: AssessmentRolesDescription[role],
  }));

export type IAssessmentRole = {
  roleId: number;
  name: AssessmentRoleEnum;
};

export type IAssessmentPermissions = {
  assessmentId: number;
  userId: number;
  roleId: number;

  user?: IUser;
  role?: IAssessmentRole;
};

export class AssessmentPermissionsHttpService {
  static async getAssessmentUsers(assessmentId: number): Promise<IAssessmentPermissions[]> {
    const { data: assessmentPermissions } = await httpClient.get(`/api/assessments/${assessmentId}/permissions`);
    return assessmentPermissions;
  }

  static async manageUserAssessmentPermissions(
    assessmentId: number,
    { userId, role }: { userId: number; role: AssessmentRoleEnum },
  ): Promise<IAssessmentPermissions> {
    const { data: assessmentUser } = await httpClient.patch(`/api/assessments/${assessmentId}/permissions`, {
      userId,
      role,
    });
    return assessmentUser;
  }

  static async removeUserAssessmentPermissions(assessmentId: number, userId: number): Promise<IAssessmentPermissions> {
    const { data: removeResult } = await httpClient.delete(
      `/api/assessments/${assessmentId}/permissions/users/${userId}`,
    );
    return removeResult;
  }
}
